import { Toggle } from '@cimpress/react-components';
import { useContext, useEffect, useState } from 'react';
import { getTransferStyleUrl } from '../api';
import { ApiKeyContext } from '../App';
import { CardBuilder, Control, Page, PreviewRow, PreviewSection } from '../Components';
import type { Card } from '../Components/common';
import { TransferStyleControls } from '../Components/TransferStyle';
import { useTransferStyleParams } from '../Components/TransferStyle/useTransferStyleParams';
import { getUrl } from '../tools';

const intro = `This is the page to test our transfer style endpoint.\nFill in the source and target to see the transfer result`;

export const TransferStyle = () => {
    const transferStyleParams = useTransferStyleParams();
    const [previewRows, setPreviewRows] = useState<Card[][]>([]);
    const apiKey = useContext(ApiKeyContext);

    useEffect(() => {
        const rows: Card[][] = [];
        const originRow = PreviewRow();

        if (transferStyleParams.sourceUrl) {
            const sourceCard = CardBuilder.ofType('documentUrl')
                .withTitle('Source Document')
                .withData({ documentUrl: getUrl(transferStyleParams.sourceUrl) ?? '' })
                .build();
            originRow.push(sourceCard);
        }

        if (transferStyleParams.targetUrl) {
            const targetCard = CardBuilder.ofType('documentUrl')
                .withTitle('Target Document')
                .withData({ documentUrl: getUrl(transferStyleParams.targetUrl) ?? '' })
                .build();
            originRow.push(targetCard);
        }

        rows.push(originRow);

        if (transferStyleParams.sourceUrl && transferStyleParams.targetUrl) {
            const resultCard = CardBuilder.ofType('documentUrl')
                .withTitle('Result Document')
                .withData({
                    documentUrl:
                        getTransferStyleUrl(
                            transferStyleParams.sourceUrl,
                            transferStyleParams.targetUrl,
                            apiKey,
                            transferStyleParams.fixLogoContrast,
                            transferStyleParams.enableMulticolorLogoContrastFix,
                        ) ?? '',
                    backgroundColor: 'FFFFFF',
                })
                .build();
            const resultRow = PreviewRow(resultCard);
            rows.push(resultRow);
        }

        setPreviewRows(rows);
    }, [
        transferStyleParams.sourceUrl,
        transferStyleParams.targetUrl,
        apiKey,
        transferStyleParams.fixLogoContrast,
        transferStyleParams.enableMulticolorLogoContrastFix,
    ]);

    return (
        <Page>
            <Page.Title>Transfer Style</Page.Title>

            <Page.Controls>
                <TransferStyleControls
                    sourceUrl={transferStyleParams.sourceUrl}
                    targetUrl={transferStyleParams.targetUrl}
                    setSourceUrl={transferStyleParams.setSourceUrl}
                    setTargetUrl={transferStyleParams.setTargetUrl}
                />

                <Control>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <Toggle
                            on={transferStyleParams.fixLogoContrast}
                            onClick={() => transferStyleParams.setFixLogoContrast(!transferStyleParams.fixLogoContrast)}
                            size="sm"
                        />
                        <span style={{ marginLeft: '10px' }}>Fix Logo Contrast</span>
                    </div>
                </Control>

                <Control>
                    <div style={{ alignItems: 'center', display: 'flex' }}>
                        <Toggle
                            on={transferStyleParams.enableMulticolorLogoContrastFix}
                            onClick={() =>
                                transferStyleParams.setEnableMulticolorLogoContrastFix(
                                    !transferStyleParams.enableMulticolorLogoContrastFix,
                                )
                            }
                            size="sm"
                        />
                        <span style={{ marginLeft: '10px' }}>Enable Multicolor Logo Contrast Fix</span>
                    </div>
                </Control>
            </Page.Controls>

            <Page.Content>
                <PreviewSection
                    introduction={intro}
                    showIntro={!transferStyleParams.sourceUrl && !transferStyleParams.targetUrl}
                    rows={previewRows}
                />
            </Page.Content>
        </Page>
    );
};
